'use client';

import React from 'react';
import { PanelRightClose, Sparkles, Camera, Network } from 'lucide-react';

export type SidePanelMode = 'none' | 'ai' | 'photos' | 'details';

interface SidePanelToggleProps {
  mode: SidePanelMode;
  onChange: (mode: SidePanelMode) => void;
  isDark?: boolean;
  /** Hide options that aren't applicable (e.g. hide AI if no permission) */
  hideAI?: boolean;
  hidePhotos?: boolean;
  hideDetails?: boolean;
}

const TOGGLE_OPTIONS: Array<{
  value: SidePanelMode;
  label: string;
  shortLabel: string;
  icon: React.ElementType;
  title: string;
}> = [
  { value: 'none', label: 'None', shortLabel: 'Off', icon: PanelRightClose, title: 'No side panel' },
  { value: 'ai', label: 'AI', shortLabel: 'AI', icon: Sparkles, title: 'AI Finance Assistant' },
  { value: 'photos', label: 'Photos', shortLabel: 'Photos', icon: Camera, title: 'Photo & attachment panel' },
  { value: 'details', label: 'Details', shortLabel: 'Tree', icon: Network, title: 'Relationship tree & details' },
];

export default function SidePanelToggle({
  mode,
  onChange,
  isDark = false,
  hideAI = false,
  hidePhotos = false,
  hideDetails = false,
}: SidePanelToggleProps) {
  const hiddenSet = new Set<SidePanelMode>();
  if (hideAI) hiddenSet.add('ai');
  if (hidePhotos) hiddenSet.add('photos');
  if (hideDetails) hiddenSet.add('details');

  const visibleOptions = TOGGLE_OPTIONS.filter((opt) => !hiddenSet.has(opt.value));

  return (
    <div
      className={`flex w-full items-center gap-0.5 rounded-lg p-0 ${isDark ? 'bg-white/10' : 'bg-black/10'}`}
      role="radiogroup"
      aria-label="Side panel"
    >
      {visibleOptions.map((opt) => {
        const isActive = mode === opt.value;
        const Icon = opt.icon;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={isActive}
            onClick={() => onChange(opt.value)}
            className={`flex flex-1 min-w-0 items-center justify-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-all ${
              isActive
                ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white'
                : isDark
                  ? 'text-white/70 hover:text-white hover:bg-white/10'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
            }`}
            title={opt.title}
          >
            <Icon size={14} className="flex-shrink-0" />
            <span className="hidden xl:inline-block truncate">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}
